import React from "react";
import dayjs from "dayjs";
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from "@react-pdf/renderer";
import {
  ACCOUNT_CONTACTS_SECTION,
  ACCOUNT_ENROLLMENT_FIELDS,
  ACCOUNT_ENROLLMENT_SECTIONS,
  formatAccountContactName,
} from "../AccountEnrollment/accountEnrollmentFields.js";
import {
  DISCOUNT_MODES,
  OFFER_TYPES,
  describeOfferType,
} from "../AccountEnrollment/accountEnrollmentPricing.js";

// Basic print layout for an enrollment request, mirroring IVRInfoPdf so the
// two downloads look like they came from the same app.

const styles = StyleSheet.create({
  page: { fontSize: 11, padding: 32, lineHeight: 1.5 },
  image: { height: 24 },
  heading: { fontSize: 16, fontWeight: "bold", color: "#333" },
  subheading: {
    fontSize: 13,
    marginTop: 14,
    marginBottom: 6,
    fontWeight: "bold",
    color: "#333",
  },
  line: { height: 1, backgroundColor: "grey", width: "100%", marginVertical: 8 },
  field: { fontWeight: "bold" },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  column: { width: "48%" },
  tableHeader: {
    display: "flex",
    flexDirection: "row",
    borderBottom: "1px solid #ccc",
    paddingBottom: 4,
    marginBottom: 6,
    fontWeight: "bold",
  },
  tableRow: { display: "flex", flexDirection: "row", marginBottom: 4 },
  cell: { flex: 1, paddingRight: 6 },
  cellNarrow: { width: 70, paddingRight: 6 },
});

const displayValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "—";
};

const formatPercent = (value) =>
  value === null || value === undefined || value === "" ? "—" : `${value}%`;

const formatCurrency = (value) => {
  const parsed = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(parsed)
    ? "—"
    : parsed.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const formatDate = (value) =>
  value ? dayjs(value).format("MM-DD-YYYY hh:mm A") : "—";

const chunkPairs = (items) => {
  const pairs = [];
  for (let index = 0; index < items.length; index += 2) {
    pairs.push(items.slice(index, index + 2));
  }
  return pairs;
};

const PricingTable = ({ pricing }) => {
  const groups = pricing?.ProductGroups || [];

  if (pricing?.OfferType === OFFER_TYPES.PRICE_PER_SQUARE) {
    return (
      <>
        <View style={styles.tableHeader}>
          <Text style={styles.cell}>Product Group</Text>
          <Text style={styles.cell}>Manufacturer</Text>
          <Text style={styles.cellNarrow}>Q Code</Text>
          <Text style={styles.cellNarrow}>Price / Sq</Text>
        </View>
        {groups.map((group) => (
          <View key={group.ProductGroupID} style={styles.tableRow}>
            <Text style={styles.cell}>{displayValue(group.ProductGroupName)}</Text>
            <Text style={styles.cell}>{displayValue(group.ManufacturerName)}</Text>
            <Text style={styles.cellNarrow}>{displayValue(group.QCode)}</Text>
            <Text style={styles.cellNarrow}>{formatCurrency(group.PricePerSquare)}</Text>
          </View>
        ))}
      </>
    );
  }

  if (pricing?.DiscountMode === DISCOUNT_MODES.DEFAULT_FOR_ALL) {
    return (
      <Text>
        {formatPercent(pricing.DefaultDiscountPercentage)} applied to all{" "}
        {groups.length} selected product group{groups.length === 1 ? "" : "s"}.
      </Text>
    );
  }

  if (pricing?.DiscountMode === DISCOUNT_MODES.PER_PRODUCT_GROUP) {
    return (
      <>
        <View style={styles.tableHeader}>
          <Text style={styles.cell}>Product Group</Text>
          <Text style={styles.cell}>Manufacturer</Text>
          <Text style={styles.cellNarrow}>Q Code</Text>
          <Text style={styles.cellNarrow}>Discount</Text>
        </View>
        {groups.map((group) => (
          <View key={group.ProductGroupID} style={styles.tableRow}>
            <Text style={styles.cell}>{displayValue(group.ProductGroupName)}</Text>
            <Text style={styles.cell}>{displayValue(group.ManufacturerName)}</Text>
            <Text style={styles.cellNarrow}>{displayValue(group.QCode)}</Text>
            <Text style={styles.cellNarrow}>{formatPercent(group.DiscountPercentage)}</Text>
          </View>
        ))}
      </>
    );
  }

  return (
    <>
      <View style={styles.tableHeader}>
        <Text style={styles.cell}>Product Group</Text>
        <Text style={styles.cell}>Product Code</Text>
        <Text style={styles.cellNarrow}>Units</Text>
        <Text style={styles.cellNarrow}>Std. Price</Text>
        <Text style={styles.cellNarrow}>Discount</Text>
      </View>
      {groups.flatMap((group) =>
        (group.Products || []).map((product) => (
          <View
            key={`${group.ProductGroupID}-${product.ProductID}`}
            style={styles.tableRow}
          >
            <Text style={styles.cell}>{displayValue(group.ProductGroupName)}</Text>
            <Text style={styles.cell}>{displayValue(product.ProductCode)}</Text>
            <Text style={styles.cellNarrow}>{displayValue(product.Units)}</Text>
            <Text style={styles.cellNarrow}>{formatCurrency(product.StandardPrice)}</Text>
            <Text style={styles.cellNarrow}>{formatPercent(product.DiscountPercentage)}</Text>
          </View>
        )),
      )}
    </>
  );
};

const AccountEnrollmentPdf = ({ detail }) => {
  const account = detail?.Account || {};
  const pricing = detail?.Pricing;
  const groups = detail?.ProductGroups || [];

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.row}>
          <Text style={styles.heading}>Account Enrollment Request</Text>
          <Image
            style={styles.image}
            src={`${process.env.PUBLIC_URL}/companyLogo.png`}
          />
        </View>
        <View style={styles.line} />

        <View style={styles.row}>
          <View style={styles.column}>
            <Text>
              <Text style={styles.field}>Request ID:</Text> {displayValue(detail?.ID)}
            </Text>
            <Text>
              <Text style={styles.field}>Status:</Text> {displayValue(detail?.Status)}
            </Text>
            <Text>
              <Text style={styles.field}>Requester:</Text>{" "}
              {displayValue(detail?.RequesterName)}
            </Text>
          </View>
          <View style={styles.column}>
            <Text>
              <Text style={styles.field}>Submitted:</Text> {formatDate(detail?.createdAt)}
            </Text>
            <Text>
              <Text style={styles.field}>Reviewer:</Text>{" "}
              {displayValue(detail?.ReviewerName)}
            </Text>
            <Text>
              <Text style={styles.field}>Reviewed:</Text> {formatDate(detail?.ReviewedAt)}
            </Text>
          </View>
        </View>

        {ACCOUNT_ENROLLMENT_SECTIONS.map((section) => (
          <View key={section.key}>
            <Text style={styles.subheading}>{section.title}</Text>
            {chunkPairs(
              ACCOUNT_ENROLLMENT_FIELDS.filter(
                (field) => field.section === section.key,
              ),
            ).map((pair, index) => (
              <View key={`${section.key}-${index}`} style={styles.row}>
                {pair.map((field) => (
                  <View key={field.key} style={styles.column}>
                    <Text>
                      <Text style={styles.field}>{field.label}:</Text>{" "}
                      {field.key === "DistributorID"
                        ? displayValue(detail?.DistributorName)
                        : field.key === "RegionID" && detail?.RegionName
                          ? displayValue(detail.RegionName)
                          : displayValue(account[field.key])}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        ))}

        <Text style={styles.subheading}>
          {ACCOUNT_CONTACTS_SECTION.title} ({(detail?.AccountContacts || []).length})
        </Text>
        {(detail?.AccountContacts || []).length ? (
          <>
            <View style={styles.tableHeader}>
              <Text style={styles.cell}>Name</Text>
              <Text style={styles.cell}>Email</Text>
              <Text style={styles.cellNarrow}>Phone</Text>
              <Text style={styles.cellNarrow}>Provider NPI</Text>
            </View>
            {detail.AccountContacts.map((contact) => (
              <View key={contact.Email} style={styles.tableRow}>
                <Text style={styles.cell}>{displayValue(formatAccountContactName(contact))}</Text>
                <Text style={styles.cell}>{displayValue(contact.Email)}</Text>
                <Text style={styles.cellNarrow}>{displayValue(contact.Phone)}</Text>
                <Text style={styles.cellNarrow}>
                  {contact.IsProvider ? displayValue(contact.ProviderNPI) : "—"}
                </Text>
              </View>
            ))}
          </>
        ) : (
          <Text>No users requested</Text>
        )}

        <View style={styles.line} />
        <Text>
          <Text style={styles.field}>BAA attached:</Text>{" "}
          {displayValue(detail?.BAAFileName) === "—" ? "No" : detail.BAAFileName}
        </Text>

        <Text style={styles.subheading}>
          Selected product groups ({groups.length})
        </Text>
        <View style={styles.tableHeader}>
          <Text style={styles.cell}>Product Group</Text>
          <Text style={styles.cell}>Manufacturer</Text>
          <Text style={styles.cellNarrow}>Q Code</Text>
        </View>
        {groups.map((group) => (
          <View key={group.ProductGroupID} style={styles.tableRow}>
            <Text style={styles.cell}>{displayValue(group.ProductGroupName)}</Text>
            <Text style={styles.cell}>{displayValue(group.ManufacturerName)}</Text>
            <Text style={styles.cellNarrow}>{displayValue(group.QCode)}</Text>
          </View>
        ))}

        <Text style={styles.subheading}>
          Pricing — {describeOfferType(pricing)}
        </Text>
        <PricingTable pricing={pricing} />

        {detail?.Status === "Rejected" && detail?.RejectionReason ? (
          <View>
            <Text style={styles.subheading}>Rejection reason</Text>
            <Text>{detail.RejectionReason}</Text>
          </View>
        ) : null}
      </Page>
    </Document>
  );
};

const createAccountEnrollmentPdfBlob = async (detail) =>
  pdf(<AccountEnrollmentPdf detail={detail} />).toBlob();

export { AccountEnrollmentPdf, createAccountEnrollmentPdfBlob };
